(function () {
  'use strict';

  const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
  const ALLOWED_UPLOAD_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
  const MAX_RIBBON_CHOICES = 5;
  const RANK_LABELS = ['1st choice', '2nd choice', '3rd choice', '4th choice', '5th choice'];

  class BowBuilder {
    constructor(root) {
      this.root = root;
      const configEl = root.querySelector('[data-bow-builder-config]');
      this.config = JSON.parse(configEl.textContent);
      this.moneyFormatter = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.config.currencyCode || 'USD',
      });

      this.state = {
        step: 1,
        ribbonChoices: [],
        size: null,
        inspirationFile: null,
        ribbonProducts: [],
      };

      this.cacheDom();
      this.bindEvents();
      this.loadRibbonProducts();
    }

    cacheDom() {
      this.stepTabs = Array.from(this.root.querySelectorAll('[data-step-tab]'));
      this.stepPanels = Array.from(this.root.querySelectorAll('[data-step-panel]'));
      this.nextButtons = Array.from(this.root.querySelectorAll('[data-step-next]'));
      this.backButtons = Array.from(this.root.querySelectorAll('[data-step-back]'));

      this.inspirationInput = this.root.querySelector('[data-inspiration-input]');
      this.inspirationPreview = this.root.querySelector('[data-inspiration-preview]');
      this.inspirationPreviewImage = this.root.querySelector('[data-inspiration-preview-image]');
      this.inspirationRemove = this.root.querySelector('[data-inspiration-remove]');

      this.ribbonStatus = this.root.querySelector('[data-ribbon-status]');
      this.ribbonGrid = this.root.querySelector('[data-ribbon-grid]');
      this.ribbonCardTemplate = this.root.querySelector('[data-ribbon-card-template]');
      this.ribbonRankedList = this.root.querySelector('[data-ribbon-ranked-list]');
      this.ribbonRankedEmpty = this.root.querySelector('[data-ribbon-ranked-empty]');
      this.ribbonRankedItemTemplate = this.root.querySelector('[data-ribbon-ranked-item-template]');

      this.sizeTiles = Array.from(this.root.querySelectorAll('[data-size-tile]'));

      this.summaryRibbons = this.root.querySelector('[data-summary-ribbons]');
      this.summarySize = this.root.querySelector('[data-summary-size]');
      this.summaryInspiration = this.root.querySelector('[data-summary-inspiration]');
      this.estimatePrice = this.root.querySelector('[data-estimate-price]');

      this.form = this.root.querySelector('[data-bow-form]');
      this.customerNameInput = this.root.querySelector('[data-customer-name]');
      this.customerEmailInput = this.root.querySelector('[data-customer-email]');
      this.customerNotesInput = this.root.querySelector('[data-customer-notes]');
      this.formMessage = this.root.querySelector('[data-bow-form-message]');
      this.submitButton = this.root.querySelector('[data-bow-submit]');
    }

    bindEvents() {
      this.nextButtons.forEach((btn) => btn.addEventListener('click', () => this.goToStep(this.state.step + 1)));
      this.backButtons.forEach((btn) => btn.addEventListener('click', () => this.goToStep(this.state.step - 1)));
      this.stepTabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const target = parseInt(tab.dataset.stepTab, 10);
          if (target < this.state.step || tab.classList.contains('is-complete')) {
            this.goToStep(target);
          }
        });
      });

      this.inspirationInput.addEventListener('change', (e) => this.handleInspirationChange(e));
      this.inspirationRemove.addEventListener('click', () => this.clearInspiration());

      this.ribbonRankedList.addEventListener('click', (e) => this.handleRankedListClick(e));

      this.sizeTiles.forEach((tile) => {
        tile.addEventListener('click', () => this.setSize(tile.dataset.size));
      });

      this.customerNameInput.addEventListener('input', () => this.updateSubmitState());
      this.customerEmailInput.addEventListener('input', () => this.updateSubmitState());
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    // --- Step navigation ---

    goToStep(step) {
      if (step < 1 || step > this.stepPanels.length) return;
      if (step > this.state.step) {
        const currentGate = this.nextButtons.find(
          (btn) => this.panelForButton(btn) === this.state.step && btn.dataset.requires
        );
        if (currentGate && currentGate.disabled) return;
      }

      this.state.step = step;

      this.stepPanels.forEach((panel) => {
        panel.classList.toggle('is-active', parseInt(panel.dataset.stepPanel, 10) === step);
      });
      this.stepTabs.forEach((tab) => {
        const tabStep = parseInt(tab.dataset.stepTab, 10);
        tab.classList.toggle('is-active', tabStep === step);
        if (tabStep < step) tab.classList.add('is-complete');
      });

      if (step === 4) this.updateEstimatePanel();

      this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    panelForButton(btn) {
      const panel = btn.closest('[data-step-panel]');
      return panel ? parseInt(panel.dataset.stepPanel, 10) : null;
    }

    // --- Step 1: inspiration ---

    handleInspirationChange(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;

      if (!ALLOWED_UPLOAD_TYPES.includes(file.type)) {
        window.alert('Please upload a JPG, PNG, WEBP, or HEIC photo.');
        event.target.value = '';
        return;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        window.alert('That photo is larger than 10MB. Please choose a smaller file.');
        event.target.value = '';
        return;
      }

      this.state.inspirationFile = file;
      const reader = new FileReader();
      reader.onload = () => {
        this.inspirationPreviewImage.src = reader.result;
        this.inspirationPreview.hidden = false;
      };
      reader.readAsDataURL(file);
    }

    clearInspiration() {
      this.state.inspirationFile = null;
      this.inspirationInput.value = '';
      this.inspirationPreview.hidden = true;
      this.inspirationPreviewImage.src = '';
    }

    // --- Step 2: ribbon ---

    async loadRibbonProducts() {
      const { workerApiBase, ribbonCollectionHandle } = this.config;

      if (!workerApiBase) {
        this.setRibbonStatus('Ribbon inventory isn’t connected yet. Add the backend API base URL in the theme editor.', true);
        return;
      }

      try {
        const response = await fetch(
          `${workerApiBase}/api/ribbons?collection=${encodeURIComponent(ribbonCollectionHandle || 'ribbon')}`
        );
        const json = await response.json();
        if (!response.ok) throw new Error(json.error || 'Failed to load ribbons');

        this.state.ribbonProducts = json.products || [];
        if (this.state.ribbonProducts.length === 0) {
          this.setRibbonStatus('No ribbons are available right now. Please check back soon.', true);
          return;
        }
        this.renderRibbonGrid();
        this.setRibbonStatus('');
      } catch (err) {
        console.error('Failed to load ribbon inventory', err);
        this.setRibbonStatus('We couldn’t load ribbon inventory. Please refresh the page.', true);
      }
    }

    setRibbonStatus(message, isError) {
      this.ribbonStatus.textContent = message;
      this.ribbonStatus.hidden = !message;
      if (isError) {
        this.ribbonStatus.dataset.state = 'error';
      } else {
        delete this.ribbonStatus.dataset.state;
      }
    }

    renderRibbonGrid() {
      this.ribbonGrid.innerHTML = '';
      this.state.ribbonProducts.forEach((product) => {
        const card = this.ribbonCardTemplate.content.firstElementChild.cloneNode(true);
        card.dataset.variantId = product.variantId;

        const img = card.querySelector('[data-ribbon-card-image]');
        img.src = product.imageUrl;
        img.alt = product.title;

        card.querySelector('[data-ribbon-card-title]').textContent = product.title;
        card.querySelector('[data-ribbon-card-price]').textContent = this.moneyFormatter.format(product.price);

        const badge = card.querySelector('[data-ribbon-card-badge]');
        if (typeof product.quantityAvailable === 'number' && product.quantityAvailable <= 0) {
          badge.hidden = false;
        }

        card.addEventListener('click', () => this.handleRibbonCardClick(product));
        this.ribbonGrid.appendChild(card);
      });
      this.refreshRibbonCardStates();
    }

    handleRibbonCardClick(product) {
      const existingIndex = this.state.ribbonChoices.findIndex((p) => p.variantId === product.variantId);
      if (existingIndex !== -1) {
        this.state.ribbonChoices.splice(existingIndex, 1);
      } else {
        if (this.state.ribbonChoices.length >= MAX_RIBBON_CHOICES) {
          this.setRibbonStatus(`You can rank up to ${MAX_RIBBON_CHOICES} ribbons. Remove one to add another.`, true);
          return;
        }
        this.state.ribbonChoices.push(product);
      }
      this.setRibbonStatus('');
      this.refreshRibbonCardStates();
      this.renderRankedList();
      this.updateNextButtonGates();
    }

    moveRibbonChoice(index, delta) {
      const target = index + delta;
      if (target < 0 || target >= this.state.ribbonChoices.length) return;
      const choices = this.state.ribbonChoices;
      [choices[index], choices[target]] = [choices[target], choices[index]];
      this.refreshRibbonCardStates();
      this.renderRankedList();
    }

    removeRibbonChoice(index) {
      this.state.ribbonChoices.splice(index, 1);
      this.setRibbonStatus('');
      this.refreshRibbonCardStates();
      this.renderRankedList();
      this.updateNextButtonGates();
    }

    handleRankedListClick(event) {
      const item = event.target.closest('[data-ribbon-ranked-item]');
      if (!item) return;
      const index = Array.from(this.ribbonRankedList.children).indexOf(item);

      if (event.target.closest('[data-ribbon-ranked-up]')) {
        this.moveRibbonChoice(index, -1);
      } else if (event.target.closest('[data-ribbon-ranked-down]')) {
        this.moveRibbonChoice(index, 1);
      } else if (event.target.closest('[data-ribbon-ranked-remove]')) {
        this.removeRibbonChoice(index);
      }
    }

    refreshRibbonCardStates() {
      Array.from(this.ribbonGrid.children).forEach((card) => {
        const rankIndex = this.state.ribbonChoices.findIndex((p) => p.variantId === card.dataset.variantId);
        const isSelected = rankIndex !== -1;
        card.classList.toggle('is-selected', isSelected);
        const rankEl = card.querySelector('[data-ribbon-card-rank]');
        rankEl.hidden = !isSelected;
        rankEl.textContent = isSelected ? String(rankIndex + 1) : '';
      });
    }

    renderRankedList() {
      Array.from(this.ribbonRankedList.querySelectorAll('[data-ribbon-ranked-item]')).forEach((el) => el.remove());
      this.ribbonRankedEmpty.hidden = this.state.ribbonChoices.length > 0;

      this.state.ribbonChoices.forEach((product, index) => {
        const item = this.ribbonRankedItemTemplate.content.firstElementChild.cloneNode(true);
        item.querySelector('[data-ribbon-ranked-position]').textContent = RANK_LABELS[index] || `${index + 1}th choice`;
        item.querySelector('[data-ribbon-ranked-title]').textContent = product.title;
        item.querySelector('[data-ribbon-ranked-up]').disabled = index === 0;
        item.querySelector('[data-ribbon-ranked-down]').disabled = index === this.state.ribbonChoices.length - 1;
        this.ribbonRankedList.appendChild(item);
      });
    }

    // --- Step 3: size ---

    setSize(size) {
      this.state.size = size;
      this.sizeTiles.forEach((tile) => {
        const selected = tile.dataset.size === size;
        tile.classList.toggle('is-selected', selected);
        tile.setAttribute('aria-checked', String(selected));
      });
      this.updateNextButtonGates();
    }

    // --- Gating ---

    updateNextButtonGates() {
      this.nextButtons.forEach((btn) => {
        if (btn.dataset.requires === 'ribbon-choice') {
          btn.disabled = this.state.ribbonChoices.length === 0;
        }
        if (btn.dataset.requires === 'size') {
          btn.disabled = !this.state.size;
        }
      });
    }

    // --- Step 4: estimate ---

    calculateEstimate() {
      if (this.state.ribbonChoices.length === 0) return 0;
      const total = this.state.ribbonChoices.reduce((sum, product) => sum + product.price, 0);
      return total / this.state.ribbonChoices.length;
    }

    updateEstimatePanel() {
      this.summaryRibbons.innerHTML = '';
      if (this.state.ribbonChoices.length === 0) {
        this.summaryRibbons.textContent = '—';
      } else {
        const list = document.createElement('ol');
        list.className = 'bow-estimate__ribbon-list';
        this.state.ribbonChoices.forEach((product) => {
          const li = document.createElement('li');
          li.textContent = product.title;
          list.appendChild(li);
        });
        this.summaryRibbons.appendChild(list);
      }

      this.summarySize.textContent = this.state.size
        ? this.state.size.charAt(0).toUpperCase() + this.state.size.slice(1)
        : '—';
      this.summaryInspiration.textContent = this.state.inspirationFile ? this.state.inspirationFile.name : 'None';
      this.estimatePrice.textContent = this.moneyFormatter.format(this.calculateEstimate());
      this.updateSubmitState();
    }

    updateSubmitState() {
      const hasName = this.customerNameInput.value.trim().length > 0;
      const hasValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.customerEmailInput.value.trim());
      this.submitButton.disabled = !(this.state.ribbonChoices.length > 0 && this.state.size && hasName && hasValidEmail);
    }

    setFormMessage(message, state) {
      this.formMessage.textContent = message;
      if (state) {
        this.formMessage.dataset.state = state;
      } else {
        delete this.formMessage.dataset.state;
      }
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (this.submitButton.disabled) return;

      const { workerApiBase } = this.config;
      if (!workerApiBase) {
        this.setFormMessage('This form isn’t connected to the order backend yet. Please contact us directly.', 'error');
        return;
      }

      this.submitButton.disabled = true;
      this.setFormMessage('Submitting your request…');

      try {
        let inspirationImageUrl = null;
        if (this.state.inspirationFile) {
          inspirationImageUrl = await this.uploadInspirationPhoto(workerApiBase, this.state.inspirationFile);
        }

        const response = await fetch(`${workerApiBase}/api/submit-bow-request`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            rankedVariantIds: this.state.ribbonChoices.map((product) => product.variantId),
            size: this.state.size,
            customerName: this.customerNameInput.value.trim(),
            customerEmail: this.customerEmailInput.value.trim(),
            customerNotes: this.customerNotesInput.value.trim() || null,
            inspirationImageUrl,
          }),
        });

        const result = await response.json();
        if (!response.ok || !result.ok) {
          throw new Error(result.error || 'Something went wrong.');
        }

        this.setFormMessage(
          `Thank you! We’ve received your custom bow request (${result.draftOrderName}). We’ll follow up with a final invoice shortly.`,
          'success'
        );
        this.submitButton.textContent = 'Request sent';
      } catch (err) {
        console.error('Bow request submission failed', err);
        this.setFormMessage('We couldn’t submit your request. Please try again.', 'error');
        this.submitButton.disabled = false;
      }
    }

    async uploadInspirationPhoto(workerApiBase, file) {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${workerApiBase}/api/upload-inspiration`, {
        method: 'POST',
        body: formData,
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Photo upload failed.');
      }
      return result.fileUrl;
    }
  }

  function init() {
    document.querySelectorAll('[data-bow-builder]').forEach((root) => new BowBuilder(root));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
